import { useEffect, useMemo, useState } from 'react';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Loader2,
  MapPin,
  Search,
  X,
} from 'lucide-react';
import { DayPicker } from 'react-day-picker';
import type { DateRange } from 'react-day-picker';
import 'react-day-picker/style.css';
import type { FacilityRecord, PlaceSummary } from '../types';
import { usePlacesSummary, useFacilitiesForPlace, useCreateRequest, useCreateRequestBatch, useUserPlan } from '../hooks/useApi';
import { trackCreateRequest } from '../lib/posthog';

interface RequestWizardProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

type SiteTypeOption = 'any' | 'rv' | 'tent_rv' | 'tent' | 'other';
type ElectricOption = 'any' | 'electric' | 'non_electric';

const TOTAL_STEPS = 5;

const WEEKDAY_OPTIONS = [
  { value: 0, label: 'Sun' },
  { value: 1, label: 'Mon' },
  { value: 2, label: 'Tue' },
  { value: 3, label: 'Wed' },
  { value: 4, label: 'Thu' },
  { value: 5, label: 'Fri' },
  { value: 6, label: 'Sat' },
];

const today = new Date();

export function RequestWizard({ open, onClose, onCreated }: RequestWizardProps) {
  const { data: placeSummaries = [], isLoading: placesLoading } = usePlacesSummary();
  const createRequest = useCreateRequest();
  const createRequestBatch = useCreateRequestBatch();
  const { data: userPlan } = useUserPlan();

  const [step, setStep] = useState(1);

  // Step 1 – Park
  const [parkQuery, setParkQuery] = useState('');
  const [selectedPlace, setSelectedPlace] = useState<PlaceSummary | null>(null);

  // Step 2 – Campground (multi-select)
  const [facilityQuery, setFacilityQuery] = useState('');
  const [selectedFacilities, setSelectedFacilities] = useState<FacilityRecord[]>([]);
  const [upgradePromptVisible, setUpgradePromptVisible] = useState(false);

  // Step 3 – Dates
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: today,
    to: new Date(today.getTime() + 24 * 60 * 60 * 1000),
  });
  const [stayLength, setStayLength] = useState(2);
  const [arrivalDays, setArrivalDays] = useState<number[]>([]);

  // Step 4 – Options
  const [siteType, setSiteType] = useState<SiteTypeOption>('any');
  const [electric, setElectric] = useState<ElectricOption>('any');
  const [includeAdaSites, setIncludeAdaSites] = useState(false);
  const [water, setWater] = useState(false);
  const [sewer, setSewer] = useState(false);

  const [submitError, setSubmitError] = useState<string | null>(null);

  const { data: facilitiesForSelectedPlace = [], isLoading: facilitiesLoading } =
    useFacilitiesForPlace(selectedPlace?.placeId ?? null);

  // --- Filtered lists ---

  const filteredPlaces = useMemo(() => {
    const q = parkQuery.trim().toLowerCase();
    const sorted = [...placeSummaries].sort((a, b) => a.placeName.localeCompare(b.placeName));
    if (!q) return sorted.slice(0, 12);

    const startsWith = sorted.filter((p) => p.placeName.toLowerCase().startsWith(q));
    const includes = sorted.filter(
      (p) => !p.placeName.toLowerCase().startsWith(q) && p.placeName.toLowerCase().includes(q),
    );
    return [...startsWith, ...includes].slice(0, 20);
  }, [placeSummaries, parkQuery]);

  const sortedFacilities = useMemo(() => {
    return [...facilitiesForSelectedPlace].sort((a, b) => a.name.localeCompare(b.name));
  }, [facilitiesForSelectedPlace]);

  const filteredFacilities = useMemo(() => {
    const q = facilityQuery.trim().toLowerCase();
    if (!q) return sortedFacilities.slice(0, 20);

    const startsWith = sortedFacilities.filter((f) => f.name.toLowerCase().startsWith(q));
    const includes = sortedFacilities.filter(
      (f) => !f.name.toLowerCase().startsWith(q) && f.name.toLowerCase().includes(q),
    );
    return [...startsWith, ...includes].slice(0, 20);
  }, [sortedFacilities, facilityQuery]);

  // --- Validation ---

  const parkStepValid = selectedPlace !== null;
  const facilityStepValid = selectedFacilities.length > 0;
  const dateStepValid =
    !!dateRange?.from &&
    !!dateRange?.to &&
    dateRange.from.getTime() < dateRange.to.getTime() &&
    Number.isFinite(stayLength) &&
    stayLength > 0;

  const canAdvance =
    (step === 1 && parkStepValid) ||
    (step === 2 && facilityStepValid) ||
    (step === 3 && dateStepValid) ||
    step === 4;

  // --- Helpers ---

  const resetForm = () => {
    setStep(1);
    setParkQuery('');
    setSelectedPlace(null);
    setFacilityQuery('');
    setSelectedFacilities([]);
    setUpgradePromptVisible(false);
    setDateRange({
      from: today,
      to: new Date(today.getTime() + 24 * 60 * 60 * 1000),
    });
    setStayLength(2);
    setArrivalDays([]);
    setSiteType('any');
    setElectric('any');
    setIncludeAdaSites(false);
    setWater(false);
    setSewer(false);
    setSubmitError(null);
  };

  const toggleArrivalDay = (day: number) => {
    setArrivalDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort((a, b) => a - b),
    );
  };

  // Reset form when the modal opens.
  useEffect(() => {
    if (open) {
      resetForm();
    }
  }, [open]);

  // Close on Escape key.
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const isPro = userPlan?.tier === 'pro';

  const onSelectPlace = (place: PlaceSummary) => {
    setSelectedPlace(place);
    setParkQuery(place.placeName);
    // Clear facility selection when park changes.
    setSelectedFacilities([]);
    setFacilityQuery('');
    setUpgradePromptVisible(false);
  };

  const onToggleFacility = (facility: FacilityRecord) => {
    setSubmitError(null);
    setSelectedFacilities((current) => {
      const alreadySelected = current.some((f) => f.id === facility.id);
      if (alreadySelected) {
        setUpgradePromptVisible(false);
        return current.filter((f) => f.id !== facility.id);
      }
      // Free users can only select one facility
      if (!isPro && current.length >= 1) {
        setUpgradePromptVisible(true);
        return current;
      }
      setUpgradePromptVisible(false);
      return [...current, facility];
    });
  };

  const onSelectAllFacilities = () => {
    if (!isPro) {
      setUpgradePromptVisible(true);
      return;
    }
    setSelectedFacilities([...filteredFacilities]);
    setUpgradePromptVisible(false);
  };

  const onDeselectAllFacilities = () => {
    setSelectedFacilities([]);
    setUpgradePromptVisible(false);
  };

  const formatDate = (d: Date) => d.toISOString().slice(0, 10);

  const isSubmitting = createRequest.isPending || createRequestBatch.isPending;

  const onSubmit = async () => {
    if (selectedFacilities.length === 0 || !dateRange?.from || !dateRange?.to) return;

    setSubmitError(null);

    try {
      if (selectedFacilities.length === 1) {
        await createRequest.mutateAsync({
          facilityId: selectedFacilities[0].id,
          startDate: formatDate(dateRange.from),
          endDate: formatDate(dateRange.to),
          stayLength,
          arrivalDays,
          siteType,
          electric,
          water,
          sewer,
          includeAdaSites,
          isActive: true,
        });
      } else {
        await createRequestBatch.mutateAsync({
          facilityIds: selectedFacilities.map((f) => f.id),
          startDate: formatDate(dateRange.from),
          endDate: formatDate(dateRange.to),
          stayLength,
          arrivalDays,
          siteType,
          electric,
          water,
          sewer,
          includeAdaSites,
          isActive: true,
        });
      }

      for (const facility of selectedFacilities) {
        trackCreateRequest(facility.id);
      }
      onCreated?.();
      onClose();
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to create request');
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/40" onClick={onClose} />
      <div className="relative z-10 w-full max-w-2xl max-h-[90vh] overflow-y-auto bg-white rounded-xl shadow-xl p-6 mx-4">
        <button
          type="button"
          onClick={onClose}
          className="absolute top-4 right-4 p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
        >
          <X className="w-5 h-5" />
        </button>

        <div className="mb-6">
          <h2 className="text-xl font-bold text-gray-900">New Availability Request</h2>
          <p className="text-sm text-gray-600 mt-1">Step {step} of {TOTAL_STEPS}</p>
        </div>

        <div className="mb-6 grid grid-cols-5 gap-2">
          {Array.from({ length: TOTAL_STEPS }, (_, i) => i + 1).map((stepNumber) => (
            <div
              key={stepNumber}
              className={`h-2 rounded-full ${
                stepNumber <= step ? 'bg-emerald-500' : 'bg-gray-200'
              }`}
            />
          ))}
        </div>

        {/* Step 1 – Select Park */}
        {step === 1 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Park</label>
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={parkQuery}
                  onChange={(event) => {
                    setParkQuery(event.target.value);
                    if (selectedPlace && event.target.value !== selectedPlace.placeName) {
                      setSelectedPlace(null);
                      setSelectedFacilities([]);
                      setFacilityQuery('');
                    }
                  }}
                  placeholder="Type to search parks"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                />
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 max-h-72 overflow-auto">
              {placesLoading ? (
                <div className="p-4 text-sm text-gray-600 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading parks...
                </div>
              ) : filteredPlaces.length === 0 ? (
                <div className="p-4 text-sm text-gray-600">No matching parks.</div>
              ) : (
                filteredPlaces.map((place) => {
                  const selected = selectedPlace?.placeId === place.placeId;
                  return (
                    <button
                      key={place.placeId}
                      type="button"
                      onClick={() => onSelectPlace(place)}
                      className={`w-full text-left px-4 py-3 border-b last:border-b-0 transition-colors ${
                        selected
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'hover:bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="font-medium text-gray-900">{place.placeName}</div>
                      <div className="text-xs text-gray-500">Park</div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Step 2 – Select Campground(s) */}
        {step === 2 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-700">
              <span className="font-medium text-gray-900">Park:</span> {selectedPlace?.placeName}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="block text-sm font-medium text-gray-700">Campground{isPro ? 's' : ''}</label>
                {selectedFacilities.length > 0 && (
                  <span className="text-xs font-medium text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full">
                    {selectedFacilities.length} selected
                  </span>
                )}
              </div>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  value={facilityQuery}
                  onChange={(event) => setFacilityQuery(event.target.value)}
                  placeholder="Type to search campgrounds"
                  className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
                />
              </div>
            </div>

            {upgradePromptVisible && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Selecting multiple campgrounds requires a Pro plan.{' '}
                <a href="/billing" className="font-medium underline hover:text-amber-900">Upgrade to Pro</a>
              </div>
            )}

            {isPro && filteredFacilities.length > 1 && (
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onSelectAllFacilities}
                  className="text-xs text-emerald-700 hover:text-emerald-800 font-medium"
                >
                  Select All
                </button>
                <span className="text-xs text-gray-300">|</span>
                <button
                  type="button"
                  onClick={onDeselectAllFacilities}
                  className="text-xs text-gray-500 hover:text-gray-700 font-medium"
                >
                  Deselect All
                </button>
              </div>
            )}

            <div className="rounded-lg border border-gray-200 max-h-72 overflow-auto">
              {facilitiesLoading ? (
                <div className="p-4 text-sm text-gray-600 flex items-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Loading campgrounds...
                </div>
              ) : filteredFacilities.length === 0 ? (
                <div className="p-4 text-sm text-gray-600">
                  {sortedFacilities.length === 0
                    ? 'No campgrounds found for this park.'
                    : 'No matching campgrounds.'}
                </div>
              ) : (
                filteredFacilities.map((facility) => {
                  const selected = selectedFacilities.some((f) => f.id === facility.id);
                  return (
                    <button
                      key={facility.id}
                      type="button"
                      onClick={() => onToggleFacility(facility)}
                      className={`w-full text-left px-4 py-3 border-b last:border-b-0 transition-colors flex items-center gap-3 ${
                        selected
                          ? 'bg-emerald-50 border-emerald-200'
                          : 'hover:bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                          selected
                            ? 'bg-emerald-600 border-emerald-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {selected && <Check className="w-3 h-3 text-white" />}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{facility.name}</div>
                        <div className="text-xs text-gray-500">Campground</div>
                      </div>
                    </button>
                  );
                })
              )}
            </div>
          </div>
        )}

        {/* Step 3 – Dates */}
        {step === 3 && (
          <div className="space-y-4">
            <label className="block text-sm font-medium text-gray-700">
              Select your date range
            </label>

            {dateRange?.from && dateRange?.to && (
              <p className="text-sm text-gray-600">
                {dateRange.from.toLocaleDateString()} &ndash; {dateRange.to.toLocaleDateString()}
              </p>
            )}
            {dateRange?.from && !dateRange?.to && (
              <p className="text-sm text-gray-500">
                {dateRange.from.toLocaleDateString()} &ndash; select end date
              </p>
            )}

            <div className="flex justify-center overflow-x-auto">
              <DayPicker
                mode="range"
                selected={dateRange}
                onSelect={setDateRange}
                min={1}
                numberOfMonths={2}
                disabled={{ before: today }}
                defaultMonth={dateRange?.from ?? today}
                classNames={{ months: 'flex flex-nowrap gap-8' }}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Length of stay (# nights)
              </label>
              <input
                type="number"
                min={1}
                max={30}
                value={stayLength}
                onChange={(event) => {
                  const parsed = Number.parseInt(event.target.value, 10);
                  setStayLength(Number.isFinite(parsed) ? parsed : 1);
                }}
                className="w-36 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Arrive on specific weekdays (optional)
              </label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAY_OPTIONS.map((day) => {
                  const active = arrivalDays.includes(day.value);
                  return (
                    <button
                      key={day.value}
                      type="button"
                      onClick={() => toggleArrivalDay(day.value)}
                      className={`px-3 py-2 rounded-lg border text-sm transition-colors ${
                        active
                          ? 'bg-emerald-600 text-white border-emerald-600'
                          : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {day.label}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Step 4 – Options */}
        {step === 4 && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Site Type</label>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                {[
                  { value: 'any', label: 'Any' },
                  { value: 'rv', label: 'RV Sites' },
                  { value: 'tent_rv', label: 'Tent or RV' },
                  { value: 'tent', label: 'Tents Only' },
                  { value: 'other', label: 'Other' },
                ].map((option) => {
                  const active = siteType === option.value;
                  return (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => setSiteType(option.value as SiteTypeOption)}
                      className={`px-3 py-2 rounded-lg border text-sm ${
                        active
                          ? 'bg-emerald-50 border-emerald-500 text-emerald-700'
                          : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      {option.label}
                    </button>
                  );
                })}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Electric</label>
              <select
                value={electric}
                onChange={(event) => setElectric(event.target.value as ElectricOption)}
                className="w-56 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-transparent outline-none"
              >
                <option value="any">No preference</option>
                <option value="electric">Electric</option>
                <option value="non_electric">Non-electric</option>
              </select>
            </div>

            <div className="flex flex-wrap gap-4">
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={water}
                  onChange={(event) => setWater(event.target.checked)}
                  className="w-4 h-4"
                />
                Water
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={sewer}
                  onChange={(event) => setSewer(event.target.checked)}
                  className="w-4 h-4"
                />
                Sewer
              </label>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={includeAdaSites}
                  onChange={(event) => setIncludeAdaSites(event.target.checked)}
                  className="w-4 h-4"
                />
                Include ADA Sites
              </label>
            </div>

          </div>
        )}

        {/* Step 5 – Review & Submit */}
        {step === 5 && (
          <div className="space-y-4">
            <div className="rounded-lg border border-gray-200 p-4 bg-gray-50 space-y-2">
              <div className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Park:</span>{' '}
                {selectedPlace?.placeName ?? 'Not selected'}
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">
                  {selectedFacilities.length > 1 ? 'Campgrounds:' : 'Campground:'}
                </span>{' '}
                {selectedFacilities.length > 0
                  ? selectedFacilities.map((f) => f.name).join(', ')
                  : 'Not selected'}
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Dates:</span>{' '}
                {dateRange?.from?.toLocaleDateString() ?? '—'} to{' '}
                {dateRange?.to?.toLocaleDateString() ?? '—'}
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Stay length:</span> {stayLength} nights
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Arrival days:</span>{' '}
                {arrivalDays.length > 0
                  ? arrivalDays
                      .map((day) => WEEKDAY_OPTIONS.find((option) => option.value === day)?.label)
                      .join(', ')
                  : 'Any day'}
              </div>
              <div className="text-sm text-gray-700">
                <span className="font-medium text-gray-900">Options:</span>{' '}
                site type {siteType}, electric {electric}, water {water ? 'yes' : 'no'}, sewer{' '}
                {sewer ? 'yes' : 'no'}, ADA {includeAdaSites ? 'included' : 'excluded'}
              </div>
            </div>

            {submitError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {submitError}
              </div>
            )}

            <button
              type="button"
              onClick={onSubmit}
              disabled={isSubmitting}
              className="w-full py-3 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 flex items-center justify-center gap-2 font-medium"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating {selectedFacilities.length > 1 ? 'requests' : 'request'}...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Create {selectedFacilities.length > 1
                    ? `${selectedFacilities.length} Availability Requests`
                    : 'Availability Request'}
                </>
              )}
            </button>
          </div>
        )}

        {/* Navigation */}
        <div className="mt-6 flex items-center justify-between gap-3">
          <button
            type="button"
            onClick={() => setStep((current) => Math.max(1, current - 1))}
            disabled={step === 1}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          {step < TOTAL_STEPS ? (
            <button
              type="button"
              onClick={() => setStep((current) => Math.min(TOTAL_STEPS, current + 1))}
              disabled={!canAdvance}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              Next
              <ArrowRight className="w-4 h-4" />
            </button>
          ) : (
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              <Search className="w-4 h-4" />
              Start Over
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
